import { makeStyles, useTheme } from '@map/theme';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { useCountries } from '@shared/hooks/displayNames';
import useErrorMessage from '@shared/hooks/useErrorMessage';
import { compareComponents } from '@shared/util/compareComponents';
import { tryCloneWithClassName } from '@shared/util/react';
import CountryFlag from '@ui/CountryFlag';
import ChevronDownIcon from '@ui/Icon/library/ChevronDownIcon';
import {
  Children,
  ComponentType,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useState,
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import TextFormField from './TextFormField';

const useStyles = makeStyles(({ spacing }) => ({
  // eslint-disable-next-line custom-rules/no-unused-style-class
  option: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& > span': {
      marginLeft: spacing(2),
      fontSize: '1rem',
    },
  },
  flag: {
    flex: '0 0 auto',
  },
  adornmentMargin: {
    marginRight: spacing(3),
  },
}));

const Row = ({
  data,
  index,
  style,
}: ListChildComponentProps<Array<ReactElement<HTMLAttributes<HTMLElement>>>>) => {
  const child = data[index];

  if (!isValidElement(child)) {
    throw new Error('invalid child element');
  }

  return cloneElement(child, { style });
};

const ListboxComponentBase: FFC<{ role: string; children: ReactNode }> = (props, ref) => {
  const { children, role, ...other } = props;
  const { direction } = useTheme();
  const itemCount = Array.isArray(children) ? children.length : 0;
  const itemSize = 42;
  const minHeight = itemSize * Math.min(4, itemCount);
  const maxHeight = itemSize * Math.min(10, itemCount);
  const height = Math.min(maxHeight, Math.max(minHeight, window.innerHeight * 0.4)) + 20;
  const itemData = Children.toArray(children) as Array<ReactElement<HTMLAttributes<HTMLElement>>>;

  return (
    <div ref={ref} style={{ display: 'flex' }} role={role}>
      <div style={{ flex: '1 1 auto', height }} {...other}>
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <List
              innerElementType="ul"
              height={height}
              width={width}
              itemSize={itemSize}
              overscanCount={5}
              itemCount={itemCount}
              itemData={itemData}
              direction={direction}
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

const ListboxComponent = forwardRef(ListboxComponentBase);

type Props = {
  value: string;
  onChange: (value: string) => void;
  onChangeCallback?: (value: string) => void;
  onBlur?: AutocompleteProps<any, false, false, false>['onBlur'];
  label: string | ReactNode;
  margin?: 'none';
  name: string;
  errorMessageId?: string;
  helperText?: ReactNode;
  error?: boolean;
  TextFieldComponent?: ComponentType<any>;
  required?: boolean;
  disabled?: boolean;
  onFocus?: () => void;
  preferredCountry?: string;
};
const CountryAutocomplete = (props: Props) => {
  const {
    value,
    onChange,
    onChangeCallback,
    onBlur,
    label,
    errorMessageId,
    helperText,
    error: errorProp,
    margin,
    name = '',
    TextFieldComponent = TextFormField,
    required,
    onFocus,
    disabled,
    preferredCountry,
  } = props;
  const { classes, cx } = useStyles();
  const countries = useCountries(preferredCountry);
  const currentCountry = countries.find((i) => i.id === value) || null;
  const [inputValue, setInputValue] = useState(currentCountry?.text || '');
  type BoundAcProps = Required<AutocompleteProps<(typeof countries)[0], false, false, false>>;
  const renderOption = useCallback<BoundAcProps['renderOption']>(
    ({ key, ...props }, option) => (
      <li key={key} {...props}>
        <CountryFlag code={option.id} size="small" className={classes.flag} />
        <span>{option.text}</span>
      </li>
    ),
    [classes.flag],
  );
  const errorMessage = useErrorMessage(errorMessageId);

  return (
    <Autocomplete
      autoSelect
      selectOnFocus
      autoHighlight
      disabled={disabled}
      options={countries}
      classes={classes}
      value={currentCountry}
      inputValue={inputValue}
      getOptionLabel={({ text }) => text}
      ListboxComponent={ListboxComponent as ComponentType<HTMLAttributes<HTMLElement>>}
      onBlur={onBlur}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderOption={renderOption}
      popupIcon={<ChevronDownIcon />}
      onChange={(_event: any, option: (typeof countries)[0] | null) => {
        onChange(option?.id || '');
        onChangeCallback?.(option?.id || '');
      }}
      renderInput={(params) => {
        const props = {
          ...params,
          error: errorProp || !!errorMessageId,
          helperText: errorMessage ?? helperText,
          label,
          name,
        };
        if (compareComponents(TextFieldComponent, TextFormField)) {
          props.InputProps.endAdornment = tryCloneWithClassName(
            props.InputProps.endAdornment,
            classes.adornmentMargin,
            cx,
          );
        }
        return (
          <TextFieldComponent {...props} margin={margin} onFocus={onFocus} required={required} />
        );
      }}
    />
  );
};

export default CountryAutocomplete;
